'use client';
import React from 'react';
import { CTACardProps } from '@/components/card/types';
import clsx from 'clsx';
import { Link } from '@/components/link/Link';
import SerializeDescription from '@/components/serialize-description/SerializeDescription';
import { renameDomProps } from '@/utils/sanitizeDomProps';

const CTACardClasses = {
  light: {
    card: 'border-stone-200 bg-white text-brand-700',
    ctaButton:
      'bg-brand-700 justify-center !py-3 px-4 text-base text-white hover:text-white hover:bg-brand-600 rounded-lg',
    link: 'text-brand-700 hover:text-brand-700 hover:underline',
  },
  dark: {
    card: 'border-stone-700 bg-brand-700 text-white',
    ctaButton:
      'bg-white justify-center !py-3 px-4 text-base text-neutral-900 rounded-lg',
    link: 'text-white hover:text-white hover:underline',
  },
};

const CTACard: React.FC<CTACardProps> = React.memo(
  ({
    title,
    description,
    link,
    links,
    extraDescription,
    theme = 'light',
    className,
    ...rest
  }) => {
    const currentThemeClasses = CTACardClasses[theme];
    const renamedProps = renameDomProps(rest);
    return (
      <div
        className={clsx(
          'cta-card relative z-10 flex flex-col gap-4 overflow-hidden rounded-xl border p-6',
          currentThemeClasses.card,
          className,
        )}
        {...renamedProps}
      >
        {theme === 'dark' && (
          <div className="absolute bottom-0 right-0 -z-10 h-full w-full bg-brand-500 bg-gradient-radial-card opacity-60"></div>
        )}
        <h3 className="text-xl font-medium">{title}</h3>
        {description && (
          <div className={clsx({ 'content-dark': theme === 'dark' })}>
            <SerializeDescription description={description} />
          </div>
        )}
        {link && <Link {...link} />}
        {extraDescription && (
          <p className="m-0 text-base">{extraDescription}</p>
        )}

        {links && (
          <ul className="card-links flex list-none flex-col gap-2">
            {links.map((link, index) => (
              <li key={index}>{link && <Link {...link} />}</li>
            ))}
          </ul>
        )}
      </div>
    );
  },
);

CTACard.displayName = 'CTACard';

export default CTACard;
